import React from 'react';
import {
  View, Text, StyleSheet, Pressable, ViewStyle,
} from 'react-native';
import { Circle, G, Svg } from 'react-native-svg';
import Colors from '../../../constants/Colors';
import Style from '../../../constants/Style';
import { PressableState } from '../../PressableStyled';
import { comparisonLineColor } from '../../Home/HomeGraphLineSelector';

export type ProgressBarDataItem = {
  value: number,
  count?: number,
  target?: number,
  time?: string,
  title?: string,
  onPress?: () => void,
}

interface ProgressBarTileProps extends ProgressBarDataItem {
  single?: boolean,
  broad?: boolean,
  double?: boolean,
  circular?: boolean,
  large?: boolean,
  style?: ViewStyle | ViewStyle[],
  alternate?: boolean,
  color?: string,
  showCount?: boolean,
  comparisonValue?: number,
}

const ProgressBarTile = ({
  value, count, target, time, title, single, broad, double, large, circular, alternate, style,
  color, showCount, onPress, comparisonValue,
}: ProgressBarTileProps) => {
  if (circular) {
    const radius: number = large ? 218 : 60;
    const strokeWidth: number = large ? 15 : 8;
    const innerRadius: number = radius - strokeWidth;
    return <Pressable style={(state: PressableState) => [
      styles.container, large ? styles.containerLarge : (broad && styles.containerBroad),
      state.hovered && Style.listItemHovered, single && styles.containerSingle, style,
    ]}
      disabled={alternate || !onPress}
      {...{ onPress }}
    >
      {(title?.length > 0 && typeof title === 'string')
        && <Text style={styles.title}>{title}</Text>}
      <View style={{
        width: radius * 2, height: radius * 2, justifyContent: 'center', alignItems: 'center',
      }}>
        <Svg
          width={radius * 2}
          height={radius * 2}
          style={{ position: 'absolute' }}
        >
          <G
            rotation={270}
            translate={[radius, radius]}
          >
            <Circle
              stroke={Colors.light.hereworksWhite500}
              r={radius - strokeWidth}
              fill='transparent'
              {...{ strokeWidth }}
              strokeDasharray={Math.PI * (innerRadius * 2)}
              strokeDashoffset={0}
            />
            <Circle
              stroke={Colors.light.hereworksGreen400}
              r={innerRadius}
              fill='transparent'
              {...{ strokeWidth }}
              strokeDasharray={Math.PI * (innerRadius * 2)}
              strokeDashoffset={Math.PI * (innerRadius * 2) * (1 - ((value ?? 0) / 100))}
            />
          </G>
        </Svg>
        <Text style={[styles.count, styles.time, styles.circularGaugeValue]}>{value ?? '-'}</Text>
      </View>
    </Pressable>;
  }

  const comparisonValueExists: boolean = typeof comparisonValue === 'number';

  return ((showCount || typeof count === 'number' || typeof target === 'number' || !!time)
    ? <Pressable
      style={({ hovered }: PressableState) => [
        styles.container, hovered && Style.listItemHovered, single && styles.containerSingle, style,
      ]}
      disabled={alternate || !onPress}
      {...{ onPress }}
    >
      {title?.length > 0 && typeof title === 'string' && <Text style={styles.title}>{title}</Text>}
      <View style={[Style.row, alternate && styles.countSection]}>
        <Text style={[styles.count, { color }]}>
          {`${count ?? '-'}`}
          {typeof target === 'number' && <Text style={styles.target}>{`/${target}`}</Text>}
        </Text>
        {!!alternate && <Text style={styles.percentage}>
          {`${Number.isFinite(value) ? value : '-'}%`}
        </Text>}
      </View>
      {time && <Text style={[styles.count, styles.time]}>{time}</Text>}
      <View style={[styles.track, alternate && styles.trackAlternate]}>
        {typeof value === 'number'
          && <View
            style={[styles.bar, { width: `${Math.min(100, value)}%`, backgroundColor: color }]}
          />}
      </View>
      {!alternate && <Text style={styles.percentage}>
        {`${Number.isFinite(value) ? value : '-'}%`}
      </Text>}
    </Pressable> : <Pressable
      style={[
        styles.containerSimple, single && styles.containerSingle, double && styles.containerDouble,
        (!single && !double && broad) && styles.containerBroad, style,
      ]}
      disabled={alternate || !onPress}
      {...{ onPress }}
    >
      <View style={Style.row}>
        <Text style={styles.titleAlternate}>{title || '-'}</Text>
        <View style={Style.row}>
          <Text style={styles.percentageSimple}>
            {`${Number.isFinite(value) ? value : '-'}%`}
          </Text>
          {comparisonValueExists
            && <Text style={[styles.percentageSimple, styles.percentageComparison]}>
              {`\t${Number.isFinite(comparisonValue) ? comparisonValue : '-'}%`}
            </Text>}
        </View>
      </View>
      <View style={[styles.trackSimple, alternate && styles.trackAlternate]}>
        {typeof value === 'number'
          && <View style={[styles.barSimple, { width: `${Math.min(100, value)}%` }]} />}
      </View>
      {comparisonValueExists && <View
        style={[styles.trackSimple, styles.trackComparison, alternate && styles.trackAlternate]}
      >
        {comparisonValueExists && <View
          style={[
            styles.barSimple, styles.barComparison,
            { width: `${Math.min(100, comparisonValue)}%` },
          ]}
        />}
      </View>}
    </Pressable>);
};

const styles = StyleSheet.create({
  circularGaugeValue: {
    position: 'absolute',
  },
  container: {
    marginHorizontal: 8,
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#FFF',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 313,
  },
  track: {
    height: 16,
    marginVertical: 8,
    borderRadius: 8,
    alignSelf: 'stretch',
    backgroundColor: Colors.light.hereworksBlue50,
  },
  trackAlternate: {
    marginVertical: 0,
    marginTop: 8,
  },
  bar: {
    backgroundColor: Colors.light.hereworksBlue500,
    height: '100%',
    borderRadius: 8,
  },
  percentage: {
    fontSize: 12,
    lineHeight: 10,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack500,
  },
  count: {
    fontSize: 30,
    lineHeight: 30,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  target: {
    color: Colors.light.hereworksWhite700,
  },
  time: {
    color: Colors.light.hereworksBlack600,
  },
  title: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 8,
  },
  containerSimple: {
    marginHorizontal: 8,
    marginBottom: 8,
    padding: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: Colors.light.hereworksWhite100,
    justifyContent: 'space-between',
  },
  trackSimple: {
    height: 8,
    marginTop: 8,
    borderRadius: 4,
    alignSelf: 'stretch',
    backgroundColor: Colors.light.hereworksBlue50,
  },
  barSimple: {
    backgroundColor: Colors.light.hereworksDarkBlue,
    height: '100%',
    borderRadius: 4,
  },
  titleAlternate: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_500Medium',
  },
  percentageSimple: {
    fontSize: 14,
    lineHeight: 14,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksDarkBlue,
  },
  containerSingle: {
    flex: 1,
    width: '99%',
    alignSelf: 'stretch',
  },
  containerDouble: {
    width: 227,
  },
  containerBroad: {
    width: '48.5%',
  },
  containerLarge: {
    width: 435,
    marginBottom: 16,
  },
  countSection: {
    alignSelf: 'stretch',
  },
  trackComparison: {
    backgroundColor: Colors.light.hereworksYellow100,
  },
  barComparison: {
    backgroundColor: comparisonLineColor,
  },
  percentageComparison: {
    color: Colors.light.hereworksYellow600,
  },
});

export default ProgressBarTile;
